import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useRenameWorkspace } from '@/hooksApi';
import { cn } from '@/lib/utils';
import type { Workspace } from '@/types';

/** The invite modal's field metrics, so a one-field edit matches the forms beside it. */
const FIELD =
  'h-[40px] w-full rounded-md border border-solid border-sd-input bg-sd-input-bg px-3 text-[14px] text-sd-foreground outline-none focus:border-customGreen';

const MIN = 2;
const MAX = 100;

interface Props {
  workspace: Workspace;
  isOwner: boolean;
  isActive: boolean;
}

/**
 * The workspace name, and — for its owner — the pencil that edits it in place.
 *
 * Owner, 2026-09-16: "I can't see any edit workspace name in the Managed
 * Workspace view and it should be implemented in good UI UX and correct
 * function."
 *
 * ⚠️ INLINE, NOT A MODAL. It is one field with one rule. A dialog for it would
 * be heavier than the thing it edits; the invite modal is a modal because it is
 * a form.
 *
 * ⚠️ AND THE PENCIL IS RENDERED, NEVER HIDDEN, FOR A NON-OWNER — disabled with
 * a tooltip that says why. The owner's standing rule: an unusable control is
 * shown and explained, because a control that vanishes teaches nothing and
 * reads as a bug when a colleague can see it and you cannot.
 */
const WorkspaceNameEditor = ({ workspace, isOwner, isActive }: Props) => {
  const { t } = useTranslation();
  const rename = useRenameWorkspace();
  const currentName = workspace.name ?? '';

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const start = () => {
    setValue(currentName);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
    setValue(currentName);
  };

  const save = () => {
    const trimmed = value.trim();

    /*
     * Checked here so an obviously-invalid name never becomes a round trip the
     * user waits for. The SERVER is still the authority — `['required','filled',
     * 'string','between:2,100']` — and its 422 is surfaced below rather than
     * swallowed, because these two rules can drift and only one of them is real.
     */
    if (trimmed.length < MIN || trimmed.length > MAX) {
      setError(String(t('workspace.rename_length', { min: MIN, max: MAX })));
      return;
    }

    if (trimmed === currentName) {
      cancel();
      return;
    }

    rename.mutate(
      { uid: workspace.uid, name: trimmed },
      {
        onSuccess: () => {
          setEditing(false);
          setError(null);
          toast({ title: String(t('workspace.rename_saved', { name: trimmed })) });
        },
        onError: (err) => {
          // The server's sentence, in the caller's language, not a generic one.
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            String(t('workspace.rename_failed'));
          setError(message);
        },
      },
    );
  };

  if (!editing) {
    return (
      <div className='flex min-w-0 items-center gap-1.5'>
        {isActive ? (
          <Check className='h-4 w-4 shrink-0 text-customGreen' strokeWidth={3} aria-hidden />
        ) : null}

        <bdi
          dir='auto'
          className='block truncate text-[15px] font-semibold leading-5 text-sd-foreground'
        >
          {workspace.name ?? String(t('workspace.untitled'))}
        </bdi>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/*
                `span` wrapper: a disabled button fires no pointer events, so a
                tooltip attached straight to it never opens — and the tooltip IS
                the explanation for a non-owner. Without it the control is just
                mysteriously dead.
              */}
              <span className='shrink-0'>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  disabled={!isOwner}
                  aria-label={String(t('workspace.rename'))}
                  onClick={start}
                >
                  <Pencil className='h-3.5 w-3.5' aria-hidden />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {String(t(isOwner ? 'workspace.rename' : 'workspace.rename_owner_only'))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className='min-w-0'>
      <div className='flex flex-wrap items-center gap-2'>
        <input
          ref={inputRef}
          dir='auto'
          className={cn(FIELD, 'max-w-[260px] flex-1', error && 'border-red-500')}
          value={value}
          maxLength={MAX}
          disabled={rename.isLoading}
          aria-label={String(t('workspace.name_label'))}
          aria-invalid={Boolean(error)}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          /*
           * Enter saves, Escape restores. Both are what a person raised on any
           * other inline edit will try first, and an inline edit that only
           * responds to the mouse is a form pretending to be one.
           */
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
        />

        <Button size='sm' className='h-9 shrink-0' disabled={rename.isLoading} onClick={save}>
          {rename.isLoading ? (
            <Loader2 className='me-1 h-3.5 w-3.5 animate-spin' aria-hidden />
          ) : (
            <Check className='me-1 h-3.5 w-3.5' aria-hidden />
          )}
          {String(t('workspace.save'))}
        </Button>

        <Button
          size='sm'
          variant='ghost'
          className='h-9 shrink-0'
          disabled={rename.isLoading}
          onClick={cancel}
        >
          <X className='me-1 h-3.5 w-3.5' aria-hidden />
          {String(t('workspace.cancel'))}
        </Button>
      </div>

      {error ? (
        <p role='alert' className='mt-1 text-xs text-red-500'>
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default WorkspaceNameEditor;
